import { useScroll, m } from 'framer-motion';
// @mui
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { bgGradient } from 'src/theme/css';

// components
import ScrollProgress from 'src/components/scroll-progress';
import { MotionViewport, varFade } from 'src/components/animate';
import Iconify from 'src/components/iconify';
import RequestDemo from 'src/components/request-demo';
// utils
import { restaurantPainPoints, restaurantBenefits, restaurantTestimonials } from 'src/utils/data';

// ----------------------------------------------------------------------

export default function ForRestaurantsView() {
  const { scrollYProgress } = useScroll();
  const theme = useTheme();

  // Transform imported data to include resolved theme colors
  const painPoints = restaurantPainPoints.map((point) => ({
    ...point,
    color: theme.palette[point.colorKey].main,
  }));

  const benefits = restaurantBenefits.map((benefit) => ({
    ...benefit,
    color: theme.palette[benefit.colorKey].main,
  }));

  const testimonials = restaurantTestimonials;

  const handleDemoRequest = async (formData) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Demo request submitted:', formData);
  };

  return (
    <>
      <ScrollProgress scrollYProgress={scrollYProgress} />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          pt: { xs: 10, md: 15 },
          pb: { xs: 10, md: 15 },
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
                theme.palette.secondary.main,
                0.08
              )} 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, ${alpha(
                theme.palette.info.main,
                0.05
              )} 0%, transparent 50%)
            `,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/assets/background/overlay_1.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1,
            zIndex: 0,
          },
          '& .floating-shape-1': {
            position: 'absolute',
            top: '10%',
            right: '15%',
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: alpha(theme.palette.primary.main, 0.1),
            zIndex: 0,
            animation: 'float 6s ease-in-out infinite',
          },
          '& .floating-shape-2': {
            position: 'absolute',
            bottom: '20%',
            left: '10%',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: alpha(theme.palette.info.main, 0.1),
            zIndex: 0,
            animation: 'float 8s ease-in-out infinite reverse',
          },
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-20px) rotate(180deg)',
            },
          },
        }}
      >
        <Box className="floating-shape-1" />
        <Box className="floating-shape-2" />
        <Container
          maxWidth="lg"
          component={MotionViewport}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
            sx={{ textAlign: { xs: 'center', md: 'left' } }}
          >
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'flex-start' },
              }}
            >
              <m.div variants={varFade().inLeft}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 3,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  Transform Your
                  <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                    Restaurant Operations
                  </Box>
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: 'text.primary',
                    mb: 4,
                    lineHeight: 1.6,
                    textAlign: { xs: 'center', md: 'left' },
                  }}
                >
                  Streamline your supply chain, eliminate pricing inconsistencies, and deliver
                  exceptional dining experiences with Cartify&apos;s restaurant management platform.
                </Typography>
                <RequestDemo
                  buttonText="Book Your Demo Now"
                  buttonVariant="contained"
                  buttonSize="large"
                  onDemoRequest={handleDemoRequest}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: theme.customShadows.primary,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.customShadows.primary,
                    },
                  }}
                />
              </m.div>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}
            >
              <m.div variants={varFade().inRight}>
                <Box
                  component="img"
                  src="/assets/illustrations/illustration_dashboard.png"
                  alt="Restaurant Management"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    height: 'auto',
                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))',
                  }}
                />
              </m.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pain Points Section */}
      <Box sx={{ py: { xs: 10, md: 15 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                We Understand Your Challenges
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Running a restaurant comes with unique challenges. Let us help you overcome them.
              </Typography>
            </Box>
          </m.div>

          <Grid container spacing={4}>
            {painPoints.map((point, index) => (
              <Grid item xs={12} md={4} key={index}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: alpha(point.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <Iconify
                          icon={point.icon}
                          width={40}
                          height={40}
                          sx={{ color: point.color }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 2,
                        }}
                      >
                        {point.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {point.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          ...bgGradient({
            color: alpha(theme.palette.primary.main, 0.05),
            imgUrl: '/assets/background/overlay_2.jpg',
          }),
          py: { xs: 10, md: 15 },
        }}
      >
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                How Cartify Solves Your Problems
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Our comprehensive platform addresses every challenge you face in restaurant
                management.
              </Typography>
            </Box>
          </m.div>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'background.paper',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: alpha(benefit.color, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        <Iconify
                          icon={benefit.icon}
                          width={40}
                          height={40}
                          sx={{ color: benefit.color }}
                        />
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 2,
                        }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.secondary',
                          lineHeight: 1.6,
                        }}
                      >
                        {benefit.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 10, md: 15 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: '700',
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                What Restaurant Owners Say
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Join hundreds of satisfied restaurant owners who have transformed their operations
                with Cartify.
              </Typography>
            </Box>
          </m.div>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <m.div variants={varFade().inUp}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: theme.customShadows.z24,
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={3} alignItems="center">
                        <Avatar
                          src={testimonial.avatar}
                          sx={{
                            width: 80,
                            height: 80,
                            border: `4px solid ${theme.palette.primary.main}`,
                            overflow: 'hidden',
                            '& img': {
                              objectFit: 'contain',
                              width: '85%',
                              height: '85%',
                              borderRadius: '50%',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Iconify
                              key={i}
                              icon="solar:star-bold-duotone"
                              sx={{
                                color: theme.palette.warning.main,
                                width: 20,
                                height: 20,
                                mx: 0.5,
                              }}
                            />
                          ))}
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            fontStyle: 'italic',
                          }}
                        >
                          {testimonial.content}
                        </Typography>
                        <Divider sx={{ width: '100%' }} />
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            {testimonial.role}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </m.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          ...bgGradient({
            color: alpha(theme.palette.primary.main, 0.1),
            imgUrl: '/assets/background/overlay_3.jpg',
          }),
          py: { xs: 10, md: 15 },
        }}
      >
        <Container maxWidth="md" component={MotionViewport}>
          <m.div variants={varFade().inUp}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 3,
                }}
              >
                Ready to Transform Your Restaurant?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                Join thousands of restaurants that have already revolutionized their operations with
                Cartify. Book your personalized demo today and see the difference.
              </Typography>
              <RequestDemo
                buttonText="Book Your Demo Now"
                buttonVariant="contained"
                buttonSize="large"
                onDemoRequest={handleDemoRequest}
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  boxShadow: theme.customShadows.primary,
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: theme.customShadows.primary,
                  },
                }}
              />
            </Box>
          </m.div>
        </Container>
      </Box>
    </>
  );
}
